import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  schema?: object;
  noIndex?: boolean;
}

const defaultSEO = {
  title: 'RoundOne AI - Intelligent Candidate Assessment Platform',
  description: 'Transform your hiring process with RoundOne AI - The most sophisticated AI-powered platform for evaluating candidates across all roles. Get deeper insights, make better hiring decisions.',
  image: '/images/og-image.png',
  url: 'https://roundone.ai',
  type: 'website' as const,
};

const SEO: React.FC<SEOProps> = ({
  title = defaultSEO.title,
  description = defaultSEO.description,
  image = defaultSEO.image,
  url = defaultSEO.url,
  type = defaultSEO.type,
  publishedTime,
  modifiedTime,
  author,
  schema,
  noIndex = false,
}) => {
  const fullTitle = title === defaultSEO.title ? title : `${title} | RoundOne AI`;
  const fullUrl = url.startsWith('http') ? url : `https://roundone.ai${url}`;
  const fullImage = image.startsWith('http') ? image : `https://roundone.ai${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots meta tag - control indexing */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="RoundOne AI" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Article Specific Meta Tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 